import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  ApprovePaidMediaCampaignLinkRequest,
  CreatePaidMediaAccountRequest,
  CreatePaidMediaCampaignLinkRequest,
  GrowthPaidMediaAccountDto,
  GrowthPaidMediaAudienceSyncDto,
  GrowthPaidMediaCampaignLinkDto,
  GrowthPaidMediaRoasDto,
  GrowthPaidMediaSpendSnapshotDto,
  SetPaidMediaKillSwitchRequest,
  SubmitPaidMediaCampaignLinkRequest,
  SyncPaidMediaAudienceRequest
} from './growth-paid-media.models';

@Injectable({ providedIn: 'root' })
export class GrowthPaidMediaApi {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/growth/paid-media`;

  listAccounts(): Observable<GrowthPaidMediaAccountDto[]> {
    return this.http.get<GrowthPaidMediaAccountDto[]>(`${this.base}/accounts`);
  }

  createAccount(payload: CreatePaidMediaAccountRequest): Observable<GrowthPaidMediaAccountDto> {
    return this.http.post<GrowthPaidMediaAccountDto>(`${this.base}/accounts`, payload);
  }

  disableAccount(accountId: string): Observable<GrowthPaidMediaAccountDto> {
    return this.http.post<GrowthPaidMediaAccountDto>(`${this.base}/accounts/${accountId}/disable`, null);
  }

  setKillSwitch(accountId: string, payload: SetPaidMediaKillSwitchRequest): Observable<GrowthPaidMediaAccountDto> {
    return this.http.post<GrowthPaidMediaAccountDto>(`${this.base}/accounts/${accountId}/kill-switch`, payload);
  }

  listCampaignLinks(accountId?: string | null): Observable<GrowthPaidMediaCampaignLinkDto[]> {
    let params = new HttpParams();
    if (accountId) {
      params = params.set('accountId', accountId);
    }
    return this.http.get<GrowthPaidMediaCampaignLinkDto[]>(`${this.base}/campaign-links`, { params });
  }

  createCampaignLink(payload: CreatePaidMediaCampaignLinkRequest): Observable<GrowthPaidMediaCampaignLinkDto> {
    return this.http.post<GrowthPaidMediaCampaignLinkDto>(`${this.base}/campaign-links`, payload);
  }

  submitCampaignLink(linkId: string, payload: SubmitPaidMediaCampaignLinkRequest): Observable<GrowthPaidMediaCampaignLinkDto> {
    return this.http.post<GrowthPaidMediaCampaignLinkDto>(`${this.base}/campaign-links/${linkId}/submit`, payload);
  }

  approveCampaignLink(linkId: string, payload?: ApprovePaidMediaCampaignLinkRequest | null): Observable<GrowthPaidMediaCampaignLinkDto> {
    return this.http.post<GrowthPaidMediaCampaignLinkDto>(
      `${this.base}/campaign-links/${linkId}/approve`,
      payload ?? {}
    );
  }

  syncAudience(payload: SyncPaidMediaAudienceRequest): Observable<GrowthPaidMediaAudienceSyncDto> {
    return this.http.post<GrowthPaidMediaAudienceSyncDto>(`${this.base}/audiences/sync`, payload);
  }

  /** GET spend also pulls a fresh connector snapshot (no separate pull endpoint). */
  getSpend(campaignLinkId: string, periodStartUtc: string, periodEndUtc: string): Observable<GrowthPaidMediaSpendSnapshotDto[]> {
    const params = new HttpParams()
      .set('campaignLinkId', campaignLinkId)
      .set('periodStartUtc', periodStartUtc)
      .set('periodEndUtc', periodEndUtc);
    return this.http.get<GrowthPaidMediaSpendSnapshotDto[]>(`${this.base}/spend`, { params });
  }

  getRoas(campaignLinkId: string, periodStartUtc: string, periodEndUtc: string): Observable<GrowthPaidMediaRoasDto> {
    const params = new HttpParams()
      .set('campaignLinkId', campaignLinkId)
      .set('periodStartUtc', periodStartUtc)
      .set('periodEndUtc', periodEndUtc);
    return this.http.get<GrowthPaidMediaRoasDto>(`${this.base}/roas`, { params });
  }
}
